/**
 * Leitura e validacao do pacote de skill — a regra unica do AC8.
 *
 * Funcao pura, sem I/O e sem `Buffer`: o `apps/web` valida o mesmo pacote no
 * navegador antes de enviar, e o `apps/api` revalida ao registrar. Duas
 * implementacoes divergiriam no dia seguinte; foi o que aconteceu com os
 * headers do gateway (ver `@prodgy/llm-gateway-headers`).
 */
import { parseFrontmatter } from './frontmatter.js';
import { SKILL_DESCRIPTION_MAX_LENGTH, SKILL_ENTRY_FILE, SKILL_FILE_MAX_BYTES, SKILL_NAME_MAX_LENGTH, SKILL_NAME_PATTERN, SKILL_PACKAGE_MAX_BYTES, } from './limits.js';
export function readSkillPackage(input) {
    const issues = [];
    const entry = input.files.find((file) => file.path === SKILL_ENTRY_FILE);
    if (!entry) {
        return {
            ok: false,
            issues: [
                {
                    code: 'missing_skill_md',
                    field: SKILL_ENTRY_FILE,
                    message: `package has no ${SKILL_ENTRY_FILE} at its root`,
                },
            ],
        };
    }
    const parsed = parseFrontmatter(entry.content);
    if (!parsed.ok) {
        return {
            ok: false,
            issues: [
                {
                    code: parsed.reason === 'missing' ? 'missing_frontmatter' : 'invalid_frontmatter',
                    field: SKILL_ENTRY_FILE,
                    message: parsed.detail,
                },
            ],
        };
    }
    const name = typeof parsed.data.name === 'string' ? parsed.data.name.trim() : '';
    const description = typeof parsed.data.description === 'string' ? parsed.data.description.trim() : '';
    if (name === '') {
        issues.push({ code: 'name_missing', field: 'name', message: 'frontmatter has no name' });
    }
    else {
        if (name.length > SKILL_NAME_MAX_LENGTH) {
            issues.push({
                code: 'name_too_long',
                field: 'name',
                message: `name has ${name.length} characters, over the limit of ${SKILL_NAME_MAX_LENGTH}`,
            });
        }
        if (!SKILL_NAME_PATTERN.test(name)) {
            issues.push({
                code: 'name_format',
                field: 'name',
                message: `name "${name}" is not lowercase words joined by single hyphens`,
            });
        }
        if (name !== input.folderName) {
            issues.push({
                code: 'name_folder_mismatch',
                field: 'name',
                message: `name "${name}" does not match the package folder "${input.folderName}"`,
            });
        }
    }
    if (description === '') {
        issues.push({
            code: 'description_missing',
            field: 'description',
            message: 'frontmatter has no description',
        });
    }
    else if (description.length > SKILL_DESCRIPTION_MAX_LENGTH) {
        issues.push({
            code: 'description_too_long',
            field: 'description',
            message: `description has ${description.length} characters, over the limit of ${SKILL_DESCRIPTION_MAX_LENGTH}`,
        });
    }
    const resources = [];
    const seen = new Set();
    let packageBytes = byteLength(entry);
    for (const file of input.files) {
        if (file.path === SKILL_ENTRY_FILE)
            continue;
        issues.push(...pathIssues(file.path));
        // Pasta e case-insensitive em Windows e macOS: dois caminhos que so diferem
        // na caixa colidiriam ao ser exportados (AB#85640).
        const key = file.path.toLowerCase();
        if (seen.has(key)) {
            issues.push({
                code: 'resource_path_duplicate',
                field: file.path,
                message: `package carries "${file.path}" more than once`,
            });
            continue;
        }
        seen.add(key);
        const sizeBytes = byteLength(file);
        packageBytes += sizeBytes;
        if (sizeBytes > SKILL_FILE_MAX_BYTES) {
            issues.push({
                code: 'file_too_large',
                field: file.path,
                message: `file has ${sizeBytes} bytes, over the per-file limit of ${SKILL_FILE_MAX_BYTES}`,
            });
        }
        resources.push({
            path: file.path,
            contentType: file.contentType ?? contentTypeFromPath(file.path),
            sizeBytes,
            binary: file.encoding === 'base64',
        });
    }
    if (packageBytes > SKILL_PACKAGE_MAX_BYTES) {
        issues.push({
            code: 'package_too_large',
            field: input.folderName,
            message: `package has ${packageBytes} bytes, over the limit of ${SKILL_PACKAGE_MAX_BYTES}`,
        });
    }
    if (issues.length > 0)
        return { ok: false, issues };
    // Ordem estavel: o inventario servido ao agente nao pode variar entre duas
    // leituras da MESMA versao imutavel.
    resources.sort((a, b) => a.path.localeCompare(b.path));
    return {
        ok: true,
        skill: {
            name,
            description,
            title: typeof parsed.data.title === 'string' ? parsed.data.title.trim() : null,
            license: typeof parsed.data.license === 'string' ? parsed.data.license.trim() : null,
            metadata: readMetadata(parsed.data.metadata),
            requiredIntegrations: readRequiredIntegrations(parsed.data),
            requiredTools: readRequiredTools(parsed.data.metadata),
            compatibility: readCompatibility(resources),
            readmePath: findConventional(resources, README_PATTERN),
            licensePath: findConventional(resources, LICENSE_PATTERN),
            frontmatter: parsed.data,
            body: parsed.body,
            resources,
        },
    };
}
/**
 * Arquivos que o ecossistema nomeia por convencao, reconhecidos pelo nome.
 *
 * Nao estao na spec — ela define apenas `SKILL.md` —, mas sao o que um pacote de
 * terceiro de fato carrega, e deixa-los indistintos no meio dos materiais joga
 * fora informacao que quem instala precisa. A `pdf` da Anthropic, por exemplo,
 * traz um `LICENSE.txt` proprietario ao lado do procedimento.
 *
 * Reconhecer pelo nome, e nao exigir declaracao: o autor nao vai declarar nada
 * para nos, e o pacote tem de continuar valendo como esta publicado.
 */
const README_PATTERN = /^readme(\.[a-z]+)?$/i;
const LICENSE_PATTERN = /^(licen[sc]e|copying|notice)(\.[a-z]+)?$/i;
/**
 * Os arquivos de convencao de um pacote, a partir dos caminhos.
 *
 * Exportada para a api reconhece-los relendo os arquivos que ja guardou, em vez
 * de gravar os caminhos numa coluna — coluna daria margem a divergir do pacote
 * depois de uma republicacao.
 */
export function conventionalFilesOf(paths) {
    const resources = paths.map((path) => ({ path }));
    return {
        readmePath: findConventional(resources, README_PATTERN),
        licensePath: findConventional(resources, LICENSE_PATTERN),
    };
}
function findConventional(resources, pattern) {
    // Somente na RAIZ do pacote: um `examples/README.md` documenta o exemplo, e
    // tomá-lo pela documentacao do pacote seria mostrar a coisa errada.
    const match = resources.find((resource) => !resource.path.includes('/') && pattern.test(resource.path));
    return match ? match.path : null;
}
/**
 * `metadata` da spec e um mapa de texto para texto. Valor que nao e escalar e
 * descartado em vez de serializado: o consumidor e o modelo, que le o valor como
 * texto, e um objeto aninhado chegaria nele como `[object Object]`.
 */
function readMetadata(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return {};
    const entries = {};
    for (const [key, item] of Object.entries(value)) {
        if (typeof item === 'string')
            entries[key] = item;
        else if (typeof item === 'number' || typeof item === 'boolean')
            entries[key] = String(item);
    }
    return entries;
}
/**
 * Compatibilidade derivada do conteudo: um pacote com `scripts/` precisa de um
 * agente que execute codigo.
 *
 * Derivar em vez de perguntar ao autor e o que mantem o arquivo portatil — e
 * tambem o que impede a declaracao de ser otimista, ja que quem publica nao tem
 * incentivo para marcar a propria skill como mais restrita.
 */
/**
 * As integracoes exigidas, lidas de `metadata.required-integrations`.
 *
 * Aceita lista separada por virgula ou por espaco — o valor de `metadata` e
 * texto por definicao do padrao, e as duas grafias aparecem na pratica.
 *
 * Minusculas porque o que se compara do outro lado e o nome do template de
 * integracao, que e slug.
 */
function readRequiredIntegrations(frontmatter) {
    // O bloco `integrations` do topo tem precedencia (AB#85634): cada chave dele
    // e um requisito, e o conteudo diz as formas de atende-lo e o porque. Aqui so
    // os nomes interessam — a leitura rica vive no manifest do apps/api.
    const bloco = frontmatter.integrations;
    if (bloco && typeof bloco === 'object' && !Array.isArray(bloco)) {
        const ids = Object.keys(bloco)
            .map((chave) => chave.trim().toLowerCase())
            .filter((chave) => chave.length > 0);
        if (ids.length > 0)
            return [...new Set(ids)].sort();
    }
    const metadata = readMetadata(frontmatter.metadata);
    const raw = metadata['required-integrations'] ?? metadata['required_integrations'];
    if (!raw)
        return [];
    return [
        ...new Set(raw
            .split(/[\s,]+/)
            .map((name) => name.trim().toLowerCase())
            .filter((name) => name.length > 0)),
    ].sort();
}
/**
 * As tools de PLATAFORMA que a skill pede, lidas de `metadata.required-tools`.
 *
 * So as da plataforma — `knowledge_search`, `create_work_item` e afins. As que
 * vem de integracao instalada nao entram aqui: aquelas ja sao alcancadas pelo
 * requisito de integracao, e repeti-las obrigaria o autor a saber o nome das
 * tools de um template que a organizacao de destino talvez nem tenha.
 *
 * Por que em `metadata` e nao em `allowed-tools`: o campo da spec existe, mas a
 * sintaxe dele e de harness local (`Bash(git:*) Read`), nao de MCP — nao ha como
 * escrever `knowledge_search` ali sem inventar convencao. `metadata` e o lugar
 * que o padrao reserva para extensao de cliente: outro agente ignora, o Prodgy
 * le. Mesma decisao de `required-integrations`.
 *
 * Nome de tool e `a-z0-9_`: o que nao casa e descartado em silencio, porque
 * declaracao torta nao deve derrubar a leitura de um pacote de terceiro.
 */
function readRequiredTools(value) {
    const metadata = readMetadata(value);
    const raw = metadata['required-tools'] ?? metadata['required_tools'];
    if (!raw)
        return [];
    return [
        ...new Set(raw
            .split(/[\s,]+/)
            .map((name) => name.trim().toLowerCase())
            .filter((name) => /^[a-z0-9_]+$/.test(name))),
    ].sort();
}
function readCompatibility(resources) {
    const hasScripts = resources.some((resource) => resource.path === 'scripts' || resource.path.startsWith('scripts/'));
    return hasScripts ? 'harness_required' : 'universal';
}
function pathIssues(path) {
    const found = [];
    const normalized = path.split('\\').join('/');
    if (normalized.startsWith('/') || /^[A-Za-z]:\//.test(normalized)) {
        found.push({
            code: 'resource_path_absolute',
            field: path,
            message: `"${path}" is an absolute path, and a package only carries relative ones`,
        });
    }
    if (normalized.split('/').some((segment) => segment === '..')) {
        found.push({
            code: 'resource_path_traversal',
            field: path,
            message: `"${path}" walks outside the package folder`,
        });
    }
    return found;
}
/**
 * Sem `Buffer` — o pacote roda no navegador. `TextEncoder` mede UTF-8 de fato
 * (`String.length` conta unidades UTF-16 e erraria em acento e emoji), e base64
 * se mede pela formula, sem decodificar.
 */
function byteLength(file) {
    if (file.encoding === 'base64') {
        const clean = file.content.replace(/[\r\n]/g, '');
        const padding = clean.endsWith('==') ? 2 : clean.endsWith('=') ? 1 : 0;
        return Math.max(0, Math.floor((clean.length * 3) / 4) - padding);
    }
    return new TextEncoder().encode(file.content).length;
}
const CONTENT_TYPES = {
    md: 'text/markdown',
    markdown: 'text/markdown',
    txt: 'text/plain',
    json: 'application/json',
    yaml: 'application/yaml',
    yml: 'application/yaml',
    csv: 'text/csv',
    html: 'text/html',
    css: 'text/css',
    js: 'text/javascript',
    ts: 'text/plain',
    py: 'text/x-python',
    sh: 'text/x-shellscript',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    svg: 'image/svg+xml',
    pdf: 'application/pdf',
};
export function contentTypeFromPath(path) {
    const extension = path.split('.').pop()?.toLowerCase() ?? '';
    return CONTENT_TYPES[extension] ?? 'application/octet-stream';
}
//# sourceMappingURL=read.js.map